/**
 * Admin authorisation model.
 *
 * The permission catalogue and the role -> permission mapping now live in the
 * BACKEND (App\Auth\PermissionRegistry synced into the `permissions`, `roles`
 * and `role_permission` tables). The SPA no longer decides what anyone may do;
 * it asks GET /me/permissions on boot and renders from the answer.
 *
 * That is what makes the system dynamic: creating a "Responsable facturation"
 * role who may reprice but not delete companies is a change in the admin's
 * roles screen, not a frontend release.
 *
 * The `Permission` union below is kept as a COMPILE-TIME aid only - it lets
 * TypeScript catch a typo in `can("billing.pricing.updte")`. It is deliberately
 * not used to decide anything at runtime: the server's list is authoritative,
 * and a key the server knows about but this build does not is simply passed
 * through (see `isKnownPermission`).
 *
 * Nothing here is a security boundary. Any of it can be bypassed by editing
 * localStorage. The API Gates remain the only real enforcement; the point of
 * this layer is that the UI should tell the truth about what the operator can
 * do, instead of letting them discover it from a failed request.
 */

/**
 * Keys this build knows about. Must mirror App\Auth\PermissionRegistry.
 * Adding a key here without adding it server-side does nothing useful; adding
 * it server-side without adding it here still works, it just loses
 * autocomplete.
 */
export type Permission =
  // Billing - settings
  | "billing.settings.view"
  | "billing.settings.update"
  // Billing - plans
  | "billing.plan.view"
  | "billing.plan.create"
  | "billing.plan.update"
  | "billing.plan.toggle"
  // Billing - pricing (split by consequence: read / change for new customers /
  // move existing subscribers)
  | "billing.pricing.view"
  | "billing.pricing.history.view"
  | "billing.pricing.update"
  | "billing.pricing.migrate"
  // Billing - promo codes
  | "billing.promo.view"
  | "billing.promo.create"
  | "billing.promo.update"
  | "billing.promo.archive"
  // Billing - subscriptions
  | "billing.subscription.view"
  | "billing.subscription.toggle"
  | "billing.subscription.force_payment"
  // Catalogue
  | "catalogue.brand.view"
  | "catalogue.brand.manage"
  | "catalogue.service.view"
  | "catalogue.service.manage"
  | "catalogue.sign.view"
  | "catalogue.sign.manage"
  | "catalogue.pub.view"
  | "catalogue.pub.manage"
  // Appointments
  | "appointment.view"
  | "appointment.services.manage"
  | "appointment.centres.sync"
  // Fleet
  | "fleet.company.view"
  | "fleet.company.manage"
  | "fleet.user.view"
  | "fleet.user.manage"
  | "fleet.vehicle.view"
  | "fleet.vehicle.manage"
  | "fleet.billing.view"
  | "fleet.billing.manage"
  // Platform
  | "platform.parameters.manage"
  | "platform.retranscription.view"
  // Journaux — reading the trail is split from exporting it, because
  // inspecting one entry and walking out with every operator's activity
  // history are different acts.
  | "platform.audit.view"
  | "platform.audit.export"
  // IAM
  | "iam.role.view"
  | "iam.role.manage"
  | "iam.user_role.assign";

/** Human labels for the domain prefixes, used by the role editor. */
export const PERMISSION_DOMAIN_LABELS: Record<string, string> = {
  billing: "Facturation & tarifs",
  catalogue: "Catalogue",
  fleet: "Flottes",
  appointment: "Prise de RDV",
  platform: "Plateforme",
  iam: "Roles & permissions",
};

/** Shape returned by GET /me/permissions. */
export interface MePermissions {
  user_id: number | null;
  is_super_admin: boolean;
  is_active: boolean;
  company_id: number | null;
  roles: {
    slug: string;
    name: string;
    scope: "platform" | "company";
    company_id: number | null;
  }[];
  permissions: string[];
}

/** Catalogue entry returned by GET /admin/permissions. */
export interface PermissionCatalogueGroup {
  domain: string;
  label: string;
  permissions: {
    id: number;
    key: string;
    action: string;
    label: string;
    description: string | null;
    is_dangerous: boolean;
  }[];
}

export interface AdminRoleDto {
  id: number;
  slug: string;
  name: string;
  description: string | null;
  scope: "platform" | "company";
  is_system: boolean;
  is_wildcard: boolean;
  is_active: boolean;
  permissions: string[];
  users_count: number;
}

/** Shape of the user object the login flow persists into localStorage. */
export interface StoredAdminUser {
  id?: number;
  firstname?: string;
  lastname?: string;
  email?: string;
  is_active?: number;
  is_super_admin?: number;
}

const STORAGE_KEY = "currentUser";

/**
 * Read the persisted user. Returns null rather than throwing on absent or
 * corrupted JSON - a malformed localStorage entry must degrade to "no
 * permissions", never to a white screen.
 */
export const readStoredUser = (): StoredAdminUser | null => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object"
      ? (parsed as StoredAdminUser)
      : null;
  } catch {
    return null;
  }
};

/**
 * Optimistic permissions used only for the first paint, before
 * GET /me/permissions resolves.
 *
 * A legacy super-admin holds everything server-side (PermissionService treats
 * `is_super_admin` as a wildcard), so mirroring that locally avoids a flash of
 * "Acces refuse" on every reload for the people who have full access anyway.
 * Everyone else starts with nothing and waits for the server - failing closed
 * is the right default while the answer is unknown.
 */
export const optimisticPermissions = (
  user: StoredAdminUser | null
): string[] => {
  if (!user) return [];
  if (Number(user.is_active) !== 1) return [];
  // Mirrors the API Gate, which requires BOTH is_super_admin AND is_active.
  if (Number(user.is_super_admin) === 1) return ALL_PERMISSIONS;
  return [];
};

/** Every key this build knows about. */
export const ALL_PERMISSIONS: string[] = [
  "billing.settings.view",
  "billing.settings.update",
  "billing.plan.view",
  "billing.plan.create",
  "billing.plan.update",
  "billing.plan.toggle",
  "billing.pricing.view",
  "billing.pricing.history.view",
  "billing.pricing.update",
  "billing.pricing.migrate",
  "billing.promo.view",
  "billing.promo.create",
  "billing.promo.update",
  "billing.promo.archive",
  "billing.subscription.view",
  "billing.subscription.toggle",
  "billing.subscription.force_payment",
  "catalogue.brand.view",
  "catalogue.brand.manage",
  "catalogue.service.view",
  "catalogue.service.manage",
  "catalogue.sign.view",
  "catalogue.sign.manage",
  "catalogue.pub.view",
  "catalogue.pub.manage",
  "appointment.view",
  "appointment.services.manage",
  "appointment.centres.sync",
  "fleet.company.view",
  "fleet.company.manage",
  "fleet.user.view",
  "fleet.user.manage",
  "fleet.vehicle.view",
  "fleet.vehicle.manage",
  "fleet.billing.view",
  "fleet.billing.manage",
  "platform.parameters.manage",
  "platform.retranscription.view",
  "platform.audit.view",
  "platform.audit.export",
  "iam.role.view",
  "iam.role.manage",
  "iam.user_role.assign",
];

export const isKnownPermission = (key: string): key is Permission =>
  ALL_PERMISSIONS.includes(key);

/** Domain segment of a key ("billing.pricing.update" -> "billing"). */
export const domainOf = (key: string): string => key.split(".")[0] ?? "platform";
